import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Shows a dismissable "Install velvet.room" card when the browser fires
 * beforeinstallprompt (Chrome/Edge/Android). On iOS Safari the install is
 * manual — show a helper card with instructions when launched from Safari.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [dismissed, setDismissed] = useState(() =>
    typeof localStorage !== "undefined" && localStorage.getItem("velvet_pwa_dismissed") === "1"
  );

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS helper: iPhone/iPad Safari that's NOT already installed
    const ua = navigator.userAgent || "";
    const isIos = /iPhone|iPad|iPod/.test(ua);
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    const inStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIos && isSafari && !inStandalone) setShowIos(true);

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (dismissed) return null;
  const visible = !!deferred || showIos;
  if (!visible) return null;

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch {}
      setDeferred(null);
      localStorage.setItem("velvet_pwa_dismissed", "1");
      setDismissed(true);
    }
  };
  const close = () => {
    localStorage.setItem("velvet_pwa_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md rounded-2xl glass border border-fuchsia-500/30 shadow-[0_0_40px_rgba(232,28,255,0.25)] p-4 flex items-start gap-3"
    >
      <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-[#e81cff] to-[#ff2a5f] flex items-center justify-center">
        <Download className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm">
          {deferred ? "Install Xhat" : "Add to Home Screen"}
        </div>
        <div className="text-xs text-zinc-400 mt-0.5">
          {deferred ? (
            "One tap and it lives on your home screen — full screen, no browser bar."
          ) : (
            <>
              In Safari, tap <b>Share</b> → <b>Add to Home Screen</b>. Xhat will open like a native app.
            </>
          )}
        </div>
        {deferred && (
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="mt-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#e81cff] hover:bg-[#ff4dff] text-white text-sm font-semibold transition"
          >
            Install now
          </button>
        )}
      </div>
      <button
        onClick={close}
        aria-label="Dismiss"
        data-testid="pwa-install-close"
        className="text-zinc-500 hover:text-white transition"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
